// Adaptive HTTP-snapshot poll loop — mirrors iOS's SnapshotPoller
// (mobile/ios/PantherAccess/Views/Morador/CameraSnapshotAuth.swift) and Android's
// HttpSnapshotPlayerView: a sequential fetch-then-wait loop (never overlapping, since the
// next fetch is only scheduled after the previous one resolves) starting at BASE_INTERVAL_MS
// and adapting per-fetch — a slow (>900ms) or failed fetch widens toward MAX_INTERVAL_MS, a
// fast (<350ms) one narrows back toward the base. A healthy camera polls near-real-time; a
// struggling one backs off instead of getting hammered with overlapping requests.
export const SNAPSHOT_POLL_BASE_MS = 150;
export const SNAPSHOT_POLL_MAX_MS = 3000;

export interface AdaptiveSnapshotPollerHandle {
  stop: () => void;
}

// fetchOnce should perform one snapshot fetch (including updating whatever state the caller
// wants on success/failure) and return whether it succeeded — used only to drive the adaptive
// interval, not to gate whether the caller's own state gets updated.
export function startAdaptiveSnapshotPolling(
  fetchOnce: () => Promise<boolean>,
  baseIntervalMs: number = SNAPSHOT_POLL_BASE_MS,
  maxIntervalMs: number = SNAPSHOT_POLL_MAX_MS,
): AdaptiveSnapshotPollerHandle {
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let currentInterval = baseIntervalMs;

  const tick = async () => {
    if (stopped) return;
    const started = Date.now();
    let success = false;
    try {
      success = await fetchOnce();
    } catch {
      success = false;
    }
    if (stopped) return;
    const elapsed = Date.now() - started;

    if (success) {
      if (elapsed > 900) {
        currentInterval = Math.min(maxIntervalMs, currentInterval * 1.5);
      } else if (elapsed < 350) {
        currentInterval = Math.max(baseIntervalMs, currentInterval * 0.8);
      }
    } else {
      currentInterval = Math.min(maxIntervalMs, currentInterval * 2);
    }

    if (!stopped) {
      timeoutId = setTimeout(tick, currentInterval);
    }
  };

  tick(); // fire the first fetch immediately, matching iOS

  return {
    stop: () => {
      stopped = true;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
    },
  };
}
